import { cn } from 'noddi-ui-common';
import {
  applyTireDimensionConstruction,
  applyTireDimensionPartInput,
  commonTexts,
  detectTireConstruction,
  getLastTireDimensionPartReached,
  getTireDimensionPartOutsideTypicalRange,
  TIRE_CONSTRUCTION_OPTIONS,
  TIRE_DIMENSION_PART_MAX,
  TIRE_DIMENSION_PART_ORDER,
  tireDimensionPartsFromValue,
  type TireConstruction,
  type TireDimensionPart
} from 'noddi-util';
import { useId, useRef, type KeyboardEvent } from 'react';
import { FieldErrorMessage } from '../Other/FieldErrorMessage';
import { NoddiDropdown } from '../Overlay/NoddiDropdown';
import { NoddiInput } from './NoddiInput';

type TireDimensionInputTexts = typeof commonTexts.tireDimensionInput;

export type NavioTireDimensionInputProps = {
  /** The size as `205/55R16`; partial while typing, with separators kept so an emptied box holds its place. */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  /** What the size is for, e.g. "Front tires"; shown above the boxes. */
  label?: string;
  /** An error from the form; shown instead of the typical-range message. */
  errorMessage?: string;
  /** Lets the user pick R, D or B. Without it the size is radial, the only kind a customer has. */
  constructionSelectable?: boolean;
  /** Flags a fully typed part outside what a passenger car has, like a 30" rim, before the form is sent. */
  validateTypicalRange?: boolean;
  disabled?: boolean;
  translations?: Partial<TireDimensionInputTexts>;
};

// Keys people type between the parts of a size; they move on instead of landing in a digits-only box.
const SEPARATOR_KEYS = ['/', ' ', '-', '.', 'r', 'R', 'd', 'D', 'b', 'B'];

const PART_WIDTH_CLASS: Record<TireDimensionPart, string> = { width: 'w-20', profile: 'w-16', rim: 'w-20' };

/**
 * A tire size typed in three boxes, width / profile R rim, so nobody has to type the separators. Typing a full box
 * moves on, a pasted size fills every box, and Backspace in an empty box goes back.
 */
export const NavioTireDimensionInput = ({
  value,
  onChange,
  onBlur,
  name,
  label,
  errorMessage,
  constructionSelectable,
  validateTypicalRange,
  disabled,
  translations
}: NavioTireDimensionInputProps) => {
  const texts = { ...commonTexts.tireDimensionInput, ...translations };
  const id = useId();
  const refs = {
    width: useRef<HTMLInputElement>(null),
    profile: useRef<HTMLInputElement>(null),
    rim: useRef<HTMLInputElement>(null)
  };
  const parts = tireDimensionPartsFromValue(value);
  const construction: TireConstruction = constructionSelectable ? parts.construction : 'R';

  const outsideRange = validateTypicalRange ? getTireDimensionPartOutsideTypicalRange(value) : null;
  const error = errorMessage ?? (outsideRange && rangeMessage(texts, outsideRange));
  const errorId = `${id}-error`;

  const focusPart = (part: TireDimensionPart) => {
    // After the new value renders, so the caret lands behind the digits that are now in the box.
    setTimeout(() => {
      const input = refs[part].current;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    }, 0);
  };

  const handleChange = (part: TireDimensionPart, raw: string) => {
    const typedConstruction = constructionSelectable && /[RDB]/i.test(raw) ? detectTireConstruction(raw) : construction;
    const next = applyTireDimensionPartInput(value, part, raw, typedConstruction);
    onChange(next);

    // Digits past this box spill into the next ones; follow them to the last box they reached.
    const digits = raw.replace(/\D/g, '').length;
    const reached = getLastTireDimensionPartReached(part, digits);
    const following = TIRE_DIMENSION_PART_ORDER[TIRE_DIMENSION_PART_ORDER.indexOf(reached) + 1];
    if (
      digits > 0 &&
      following &&
      tireDimensionPartsFromValue(next)[reached].length === TIRE_DIMENSION_PART_MAX[reached]
    ) {
      focusPart(following);
    } else if (reached !== part) {
      focusPart(reached);
    }
  };

  const handleKeyDown = (part: TireDimensionPart, event: KeyboardEvent<HTMLInputElement>) => {
    const index = TIRE_DIMENSION_PART_ORDER.indexOf(part);
    const previous = TIRE_DIMENSION_PART_ORDER[index - 1];
    const following = TIRE_DIMENSION_PART_ORDER[index + 1];
    const input = event.currentTarget;
    const caretAtStart = input.selectionStart === 0 && input.selectionEnd === 0;
    const caretAtEnd = input.selectionStart === input.value.length;

    if (event.key === 'Backspace' && input.value === '' && previous) {
      event.preventDefault();
      onChange(applyTireDimensionPartInput(value, previous, parts[previous].slice(0, -1), construction));
      focusPart(previous);
    } else if (SEPARATOR_KEYS.includes(event.key)) {
      event.preventDefault();
      if (constructionSelectable && /^[RDB]$/i.test(event.key)) {
        onChange(applyTireDimensionConstruction(value, detectTireConstruction(event.key)));
      }
      if (following && input.value !== '') {
        focusPart(following);
      }
    } else if (event.key === 'ArrowLeft' && caretAtStart && previous) {
      event.preventDefault();
      focusPart(previous);
    } else if (event.key === 'ArrowRight' && caretAtEnd && following) {
      event.preventDefault();
      focusPart(following);
    }
  };

  const partInput = (part: TireDimensionPart) => (
    <NoddiInput
      ref={refs[part]}
      id={`${id}-${part}`}
      name={name && `${name}-${part}`}
      label={texts[part]}
      labelClassName='text-xs text-muted-foreground'
      aria-label={texts[`${part}AriaLabel`]}
      aria-invalid={!!error}
      aria-describedby={error ? errorId : undefined}
      inputMode='numeric'
      autoComplete='off'
      // An example size, so the boxes read as one even before anything is typed.
      placeholder={EXAMPLE_PARTS[part]}
      value={parts[part]}
      disabled={disabled}
      startAdornment={
        part === 'rim' && !constructionSelectable ? (
          <span aria-hidden='true' className='font-semibold text-muted-foreground'>
            R
          </span>
        ) : undefined
      }
      wrapperClassName={PART_WIDTH_CLASS[part]}
      className={cn(
        'h-11 [&_input]:font-medium',
        part === 'rim' && !constructionSelectable ? '[&_input]:pl-2' : '[&_input]:text-center',
        error && 'border-destructive'
      )}
      onBlur={onBlur}
      onChange={(event) => handleChange(part, event.target.value)}
      onKeyDown={(event) => handleKeyDown(part, event)}
    />
  );

  return (
    <div className='flex flex-col gap-2'>
      {label && (
        <span id={`${id}-label`} className='text-sm font-medium'>
          {label}
        </span>
      )}
      <div role='group' aria-labelledby={label ? `${id}-label` : undefined} className='flex items-end gap-2'>
        {partInput('width')}
        <span aria-hidden='true' className='flex h-11 items-center text-xl text-muted-foreground'>
          /
        </span>
        {partInput('profile')}
        {constructionSelectable && (
          <NoddiDropdown
            label={texts.construction}
            labelClassName='text-xs text-muted-foreground'
            tooltip={texts.constructionTooltip}
            options={TIRE_CONSTRUCTION_OPTIONS}
            selectedOption={{ value: construction, label: construction }}
            placeholder='R'
            onSelect={(option) => onChange(applyTireDimensionConstruction(value, option.value))}
            className='h-11 min-w-0 px-2'
            // Wide enough for its own label: at w-16 "Construction" overflowed
            // the box and ran into the rim field's label.
            wrapperClassName='w-28'
            menuClassName='min-w-40'
          />
        )}
        {partInput('rim')}
      </div>
      {error && <FieldErrorMessage id={errorId} errorMessage={error} />}
    </div>
  );
};

const EXAMPLE_PARTS: Record<TireDimensionPart, string> = { width: '205', profile: '55', rim: '16' };

const rangeMessage = (
  texts: TireDimensionInputTexts,
  { part, min, max }: { part: TireDimensionPart; min: number; max: number }
) => texts[`${part}OutsideTypicalRange`](min, max);
